import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Link from '@mui/material/Link';
import {
  Box,
  Typography,
  CircularProgress,
  Fade,
  Card,
  CardHeader,
  CardActions,
  CardContent,
  Chip,
  Divider,
  Stack,
  TextField,
  InputAdornment,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { IconButton, Collapse } from '@mui/material';
import ExpandCircleDownOutlinedIcon from '@mui/icons-material/ExpandCircleDownOutlined';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SyncIcon from '@mui/icons-material/Sync';
import Button from '@mui/material/Button';
import { humanReadableDateTime, titleCase } from '../../common/AppUtils'
import StaffSubstituteTable from '../../components/StaffSubstituteTable';
import Title from '../../components/Title';
import SearchBar from '../../components/SearchBar';
import EditModal from '../../components/EditModal';
import AddModal from '../../components/AddModal';
import UpgradeModal from '../../components/UpgradeModal';

import { httpRequest } from '../../api/helper';
import { useNotification } from '../../context/NotificationContext';
import { notifyClientErrors } from '../../common/AppUtils';
import { useAccount } from '../../context/AccountContext';
import { API_ERROR_MESSAGE, NA_LABEL, USER_ROLES } from '../../common/AppConstants';
import { MANAGE_SUBSCRIPTION_URL } from '../../common/AppUrls';

const TITLE = 'Staff Absence';
const API_URL = 'attendance/staff-absence/';
const SUBSTITUTE_API_URL = 'time-table/staff-substitute/'; // Constant for the Staff Substitute API endpoint
const NO_DATA_AVAILABLE_STRING =
  'No data available, please start adding now !!!';

const substituteColumns = [
  {
    attr: 'date',
    editable: false,
    extra: false,
    label: 'Date',
    related: false,
    required: true,
    type: 'text',
    help_text: 'Date of the substitute',
  },
  {
    attr: 'subject_session',
    editable: false,
    extra: false,
    label: 'Period',
    related: true,
    required: true,
    type: 'choice',
    help_text: 'Substitute session period',
  },
  {
    attr: 'substitute',
    editable: true,
    extra: false,
    label: 'Substitute',
    related: true,
    required: true,
    type: 'choice',
    help_text: 'Assigned substitute teacher',
  },
  {
    attr: 'edit',
    editable: true,
    extra: true,
    label: 'Edit',
    related: false,
    required: true,
    type: 'text',
  },
];

function preventDefault(event) {
  event.preventDefault();
}

export default function StaffAbsence() {
  const { addNotification } = useNotification();
  const navigate = useNavigate();
  const { account } = useAccount();
  const [isLoading, setIsLoading] = useState(false);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [relatedData, setRelatedData] = useState({});
  const [selectedEditRow, setSelectedEditRow] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Filtered data based on search
  const filteredRows = rows.filter(row_item =>
    relatedData.staff[row_item.staff]?.label?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddClick = () => {
    setIsAddModalOpen(true);
  };

  const handleAddModalClose = () => {
    setIsAddModalOpen(false);
  };

  const handleEditClick = (row) => {
    console.log('Edit row ', row);
    setSelectedEditRow(row);
  };

  const handleUpgradeClose = () => {
    setIsUpgradeModalOpen(false);
  };

  useEffect(() => {
    if (selectedEditRow !== null && selectedEditRow !== undefined) {
      // Only open the modal after the row has been selected
      setIsEditModalOpen(true);
    }
  }, [selectedEditRow]); // Runs when selectedEditRow changes

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setSelectedEditRow(null);
  };

  const handleEditSave = async (updatedEditRow, onEditFailure) => {
    await httpRequest({
      method: 'PUT',
      url: API_URL,
      data: updatedEditRow,
      onSuccess: (data) => {
        setRows((prevRows) =>
          prevRows.map((row, index) =>
            row.id === selectedEditRow.id ? { ...row, ...data } : row,
          ),
        );
        handleEditModalClose();
      },
      onFailure: (data) => {
        onEditFailure(data);
      },
      addNotification,
    });
  };

  const handleDelete = async (deletedRow) => {
    await httpRequest({
      method: 'DELETE',
      url: API_URL,
      data: deletedRow,
      onSuccess: (data) => {
        setRows((prevRows) =>
          prevRows.filter((row) => row.id !== deletedRow.id),
        );
      },
      addNotification,
    });
  };

  const handleAddSave = async (addFormData, onAddSuccess, onAddFailure) => {
    const response = await httpRequest({
      method: 'POST',
      url: API_URL,
      data: addFormData,
      onSuccess: (data) => {
        setRows((prevRows) => [data.entry, ...prevRows]);
        onAddSuccess();
        addNotification("Added absence");
        handleAddModalClose();
      },
      onFailure: (data) => {
        onAddFailure(data);
      },
      addNotification,
    });

    if (response.status === 403 && response.data.code === "subscription_required") {
      handleAddModalClose();  // Optional
      addNotification(`Please upgrade to proceed`);
      navigate(MANAGE_SUBSCRIPTION_URL);
      // setIsUpgradeModalOpen(true);
    }
  };

  const handleFilterApply = async () => {
    // ToDo: Filter table data
  };

  useEffect(() => {
    const fetchData = async () => {
      refreshData();
    };
    fetchData();
  }, []);

  const refreshData = async (event) => {
    setIsLoading(true);

    await httpRequest({
      method: 'GET',
      url: API_URL,
      onSuccess: (data) => {
        setColumns(data.columns);
        setRows(data.rows);
        setRelatedData(data.relatedData);
      },
      onFailure: (data) => {
        setColumns([]);
        setRows([]);
        setRelatedData({});
      },
      addNotification,
    });

    setIsLoading(false);
  };

  const AbsenceCard = ({ absence_row, absence_dict }) => {
    const [expanded, setExpanded] = useState(false);
    const [selectedSubstituteEditRow, setSelectedSubstituteEditRow] = useState(null);
    const [isSubstituteEditModalOpen, setIsSubstituteEditModalOpen] = useState(false);

    const is_approved = absence_dict.status === "approved";
    const is_rejected = absence_dict.status === "rejected";
    const chipColor =
      is_approved ? "#28B463" : (
      is_rejected ? "#CA6F1E" :
      "#555555" // "#D4AC0D"
    );

    const substituteSummary = absence_dict['substitute_required'] === false ? "Not required":
      absence_dict['substitute_status'] !== 'completed' ? "Processing" :
      absence_dict['staff_substitute_list'] === null ? "No access":
      absence_dict['staff_substitute_list'].length === 0 ? "Not applicable":
      absence_dict['staff_substitute_list'].length;

    const handleExpandClick = () => {
      setExpanded((prev) => !prev);
    };

    const handleSubstituteEditClick = (row) => {
      console.log('Edit row ', row);
      setSelectedSubstituteEditRow(row);
    };

    const handleSubstituteApproveToggle = async (susbstitute_dict, status) => {
      const requestData = {
        ...susbstitute_dict,
        status: status,
      };
      await httpRequest({
        method: 'PUT',
        url: SUBSTITUTE_API_URL,
        data: requestData,
        onSuccess: (data) => {
          setRows((prevRows) =>
            prevRows.map((row) =>
              row.id === absence_dict.id ? {
                ...row,
                staff_substitute_list: row.staff_substitute_list.map((susbstitute) =>
                  susbstitute.id === data.id ? data : susbstitute
                )
              } : row
            )
          );
        },
        addNotification,
      });
    };

    useEffect(() => {
      if (selectedSubstituteEditRow !== null && selectedSubstituteEditRow !== undefined) {
        // Only open the modal after the row has been selected
        setIsSubstituteEditModalOpen(true);
      }
    }, [selectedSubstituteEditRow]); // Runs when selectedEditRow changes

    const handleSubstituteEditModalClose = () => {
      setIsSubstituteEditModalOpen(false);
      setSelectedSubstituteEditRow(null);
    };

    const handleApproval = async (value) => {
      const finalFormData = {
        id: absence_dict.id,
        substitute_required: absence_dict.substitute_required,
        status: value,
      };

      await httpRequest({
        method: 'PATCH',
        url: API_URL,
        data: finalFormData,
        onSuccess: (data) => {
          setRows((prevRows) =>
            prevRows.map((row) =>
              row.id === absence_dict.id ? {
                ...row,
                status: data.status
              } : row
            )
          );
        },
        addNotification,
      });
    };

    const handleSubstituteEditSave = async (updatedComponentEditRow, onEditFailure) => {
      // Add the staff absence ID to the form data
      const finalFormData = {
        ...updatedComponentEditRow,
        staff_absence: absence_dict.id,
      };
      
      await httpRequest({
        method: 'PUT',
        url: SUBSTITUTE_API_URL,
        data: finalFormData,
        onSuccess: (data) => {
          setRows((prevRows) =>
            prevRows.map((row) =>
              row.id === absence_dict.id ? {
                ...row,
                staff_substitute_list: row.staff_substitute_list.map((susbstitute) =>
                  susbstitute.id === selectedSubstituteEditRow.id ? data : susbstitute
                )
              } : row
            )
          );
          handleSubstituteEditModalClose();
        },
        onFailure: (data) => {
          onEditFailure(data);
        },
        addNotification,
      });
    };

    const handleSubstituteDelete = async (deletedSubstituteRow) => {
      await httpRequest({
        method: 'DELETE',
        url: SUBSTITUTE_API_URL,
        data: deletedSubstituteRow,
        onSuccess: (data) => {
          setRows((prevRows) =>
            prevRows.map((row) =>
              row.id === absence_dict.id
                ? {
                    ...row,
                    staff_substitute_list: row.staff_substitute_list.filter(
                      (susbstitute) => susbstitute.id !== deletedSubstituteRow.id
                    ),
                  }
                : row
            )
          );
        },
        addNotification,
      });
    };

    const handleSubstituteRequiredToggle = (checked) => {
      setRows((prevRows) =>
        prevRows.map((row) =>
          row.id === absence_dict.id ? {
            ...row,
            substitute_required: checked,
          } : row
        )
      );
    }

    const AdminApprovalButtons = () => {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            width: "100%",
          }}
        >
          {/* Row 1: Substitute switch (left aligned) */}
          <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
            <Typography variant="body2">
              <Typography component="span" variant="body2" fontWeight="bold">Substitute Required: </Typography>
              <Switch
                size="small"
                checked={absence_dict.substitute_required}
                onChange={(e) => handleSubstituteRequiredToggle(e.target.checked)}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "success.light",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "success.light",
                  },
                }}
              />
            </Typography>
          </Box>

          {/* Row 2: Buttons (right aligned) */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end', // key
              alignItems: 'center',
              gap: 1.25,                     // spacing between buttons
            }}
          >
            <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={() => handleApproval('rejected')}
              sx={{ minHeight: 20, fontSize: '0.75rem' }}
              startIcon={<CancelIcon />}
            >
              Reject
            </Button>
            <Button
              size="small"
              variant="contained"
              color="success"
              onClick={() => handleApproval('approved')}
              sx={{ minHeight: 20, fontSize: '0.75rem' }}
              startIcon={<CheckCircleIcon />}
            >
              Approve
            </Button>
          </Box>
        </Box>
      );
    }

    return (
      <Box>
        <Card
          variant="outlined"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            transition: 'box-shadow 0.3s',
            '&:hover': {
              boxShadow: 3
            }
          }
        }>
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h5">{relatedData.staff[absence_dict['staff']]?.label }</Typography>
                
                {/* More data chips if needed */}
                <Chip
                  size="small"
                  label={`${absence_dict["leave_days"] ?? "x"} D`}
                  variant="outlined"
                  sx={{
                    maxHeight: 18,
                    minWidth: 36,
                    fontSize: '0.8rem',
                    // color: chipColor,
                    // borderColor: chipColor,
                  }}
                />
                
                <Chip
                  size="small"
                  label={titleCase(absence_dict["status"])}
                  variant="outlined"
                  sx={{
                    cursor: 'pointer',
                    maxHeight: 18,
                    minWidth: 36,
                    fontSize: '0.8rem',
                    color: chipColor,
                    borderColor: chipColor,
                  }}
                />
              </Box>
            }
            action={
              <IconButton onClick={() => handleEditClick(absence_dict)} size="small">
                <EditTwoToneIcon fontSize="small" />
              </IconButton>
            }
            sx={{
              px: 2,
              py: 0,
              alignItems: 'center',
              '& .MuiCardHeader-action': {
                alignSelf: 'center',
              },
            }}
            slotProps={{
              content: { sx: { py: 1.5 } },
            }}
          />
          <Divider/>
          
          <CardContent sx={{ pt: 0, pb: 1, flexGrow: 1, display: 'flex' }}>
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 0.75, pt: 2, pb: 2 }}>
              <Box>
                <Typography component="span" variant="body2" fontWeight="bold">Type: </Typography>
                <Chip
                  size="small"
                  label={relatedData.absence_type[absence_dict["absence_type"]].label}
                  variant="outlined"
                  color={
                    absence_dict["absence_type"] === "leave"
                      ? "success"
                      : "info"
                  }
                  sx={{
                    alignSelf: "flex-start",
                      maxHeight: 18,
                      minWidth: 36,
                      fontSize: '0.8rem',
                    }}
                />
              </Box>
              <Typography variant="body2">
                <Typography component="span" variant="body2" fontWeight="bold">Start:</Typography> {humanReadableDateTime(absence_dict['start_datetime']) ?? NA_LABEL}
              </Typography>
              <Typography variant="body2">
                <Typography component="span" variant="body2" fontWeight="bold">End:</Typography> {humanReadableDateTime(absence_dict['end_datetime']) ?? NA_LABEL}
              </Typography>
              
              {/* Action Buttons */}
              {account?.user?.current_role?.toLowerCase() === USER_ROLES.ADMIN && 
              !is_approved && !is_rejected && (
                <AdminApprovalButtons />
              )}

              { account?.user?.current_role?.toLowerCase() === USER_ROLES.ADMIN && is_approved && (
                <Typography variant="body2">
                  <Typography component="span" variant="body2" fontWeight="bold">Substitutes:</Typography> {substituteSummary}
                </Typography>
              )}
            </Box>

            {/* Expand Icon */}
            {/* absence_dict['staff_substitute_list'] is null for Non Admins, and is always a list for admins */}
            <CardActions disableSpacing sx={{ p: 0}}>
              { (absence_dict['staff_substitute_list'] !== null && ( is_rejected || absence_dict['staff_substitute_list'].length > 0)) && (
                <IconButton
                  size="small"
                  onClick={handleExpandClick}
                  sx={{ alignSelf: 'flex-start', mt: 1}}
                >
                  <ExpandCircleDownOutlinedIcon
                    sx={{
                      transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s'
                    }}
                  />
              </IconButton>
              )}
            </CardActions>
          </CardContent>      

          {/* Collapsible Content */}
          <Collapse in={expanded}>
            <Divider sx={{ borderStyle: 'dashed' }} />
            <CardContent sx={{ bgcolor: '#FBFBFB' }}>
              {/* Action Buttons */}
              {account?.user?.current_role?.toLowerCase() === USER_ROLES.ADMIN &&
              is_rejected && (
                <AdminApprovalButtons />
              )}

              {/* Substitutes Data */}
              { absence_dict['staff_substitute_list'] !== null && absence_dict['staff_substitute_list'].length > 0 && (
                <Stack spacing={1}>
                  {/* Add Substitutes Button */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2" alignContent={'center'}>
                      <Typography component="span" variant="body2" fontWeight="bold" sx={{ mr: 1 }}>Substitutes</Typography>
                    </Typography>
                  </Box>

                  {/* Substitute Table */}
                  <StaffSubstituteTable
                    columns={substituteColumns}
                    rows={absence_dict['staff_substitute_list']}
                    relatedData={relatedData}
                    onEdit={handleSubstituteEditClick}
                    onApproveToggle={handleSubstituteApproveToggle}
                    showPagination={false}
                  />
                </Stack>
              )}
            </CardContent>
          </Collapse>
        </Card>

        {/* Edit Substitute Modal */}
        <Box>
          <EditModal
            open={isSubstituteEditModalOpen}
            columns={substituteColumns}
            rowData={selectedSubstituteEditRow}
            relatedData={relatedData}
            handleClose={handleSubstituteEditModalClose}
            handleSave={handleSubstituteEditSave}
            handleDelete={handleSubstituteDelete}
            edit_title={`${absence_dict['code']} : Edit Substitute`}
          />
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px - 64px)', // viewport height - margings - app bar
      }}
    >
      {/* Title and Button (fixed) */}
      <Box
        sx={{
          flexShrink: 0, // Top level space distribution
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Title */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mt: 1,
            mb: 3,
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Title>{TITLE}</Title>
          </Box>
          {/* Search Bar */}
          <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Tooltip title="Refresh" arrow>
              <IconButton
                size="small"
                color="primary"
                onClick={refreshData}
                disabled={isLoading}
                sx={{
                  border: "1px solid rgba(0,0,0,0.1)",
                  borderRadius: 1,
                }}
              >
                <SyncIcon
                  fontSize="small"
                  sx={{
                    animation: isLoading ? "spin 1s linear infinite" : "none",
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(-360deg)" },
                    },
                  }}
                />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddCircleIcon />}
              onClick={handleAddClick}
              sx={{
                px: 4,
              }}
            >
              New
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Scrollable table container */}
      <Box
        sx={{
          flexGrow: 1, // This makes the table container grow and take the available space
          overflowY: 'auto', // Only the table will scroll
          // mt: 2,
          minHeight: 200, // Ensures proper space for loader
          display: 'flex',
          flexDirection: 'column', // Aligns table to the top when shown
          alignItems: isLoading ? 'center' : 'stretch', // Center loader, but stretch table
          justifyContent: isLoading ? 'center' : 'flex-start', // Center loader, start table at top
        }}
      >
        {isLoading ? (
          <Fade in={isLoading} timeout={500}>
            <CircularProgress size={50} thickness={3} />
          </Fade>
        ) : (
          <Fade in={!isLoading} timeout={500}>
            {rows.length === 0 ? (
              // Display message when there are no rows
              <Box
                sx={{
                  textAlign: 'left',
                }}
              >
                <Typography variant="h5">{NO_DATA_AVAILABLE_STRING}</Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  py: 2,
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    xl: 'repeat(2, 1fr)',
                    xxl: 'repeat(3, 1fr)',
                  },
                  gap: 1.5, // This creates even spacing without edge padding
                }}
              >
                {filteredRows.map((item_dict, index) => (
                  <Box key={index}>
                    <AbsenceCard
                      absence_row={index}
                      absence_dict={item_dict}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Fade>
        )}
      </Box>

      {/* Add Staff Absence Modal */}
      <Box>
        <AddModal
          open={isAddModalOpen}
          columns={columns}
          relatedData={relatedData}
          rows={rows}
          handleClose={handleAddModalClose}
          handleSave={handleAddSave}
          add_title="New Absence"
        />
      </Box>

      {/* Edit Staff Absence Modal */}
      <Box>
        <EditModal
          open={isEditModalOpen}
          columns={columns}
          rowData={selectedEditRow}
          relatedData={relatedData}
          handleClose={handleEditModalClose}
          handleSave={handleEditSave}
          handleDelete={handleDelete}
          edit_title="Edit Absence"
        />
      </Box>

      {/* Upgarde Modal */}
      <Box>
        <UpgradeModal
          open={isUpgradeModalOpen}
          handleClose={handleUpgradeClose}
        />
      </Box>
    </Box>
  );
}
